import React, { useRef } from 'react';
import ReactPlayer from 'react-player';
import Slider from '@mui/material/Slider';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, IconButton, Popover, Stack, Typography, Tooltip,
  MenuItem,
  Select,
} from '@mui/material';
import { Circle } from '@mui/icons-material';
import ExpertHubFullWidthWithoutTopbarLayout from 'components/templates/expert-hub-fullwidth-without-tobar.template';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import {
  AngleIcon,
  ArrowIcon,
  BackwardFillIcon,
  CircleOutlineIcon,
  FolderFillIcon,
  ForwardFillIcon,
  LineIcon,
  PauseCircleIcon,
  PencilIcon,
  PlayCircleIcon,
  RectangleOutlineIcon,
  RepeatFillIcon,
  SquareMenuIcon,
  StopCircleIcon,
  VideoSpeedFillIcon,
  VolumeFillIcon,
  VolumeMuteFillIcon,
} from 'assets/icons';
import { IMediaItemData } from 'base/common/media-modal/interface';
import { useParams } from 'react-router-dom';

import { Loader } from 'components';
import ExpertMedia from './components/expert-media';
import UserMedia from './components/user-media';
import UserMessage from './components/user-message';
import YoutubeImport from './components/youtube-import';
import TopBar from './components/top-bar';
import { sliderStyle } from './styles';
import './style.scss';
import { BrushType, Progress } from './interface';
import { format } from './uttils';
import AnnotationArea from './components/annotation-area';
import Tools from './components/tools';
import VideoPlayer from './components/video-player';
import { chatActions } from '../message-hub/slice';

import { selectConversationLoader } from '../message-hub/selectors';

const brushTypes: BrushType[] = [
  { name: 'pencil', icon: <PencilIcon />, title: 'Pencil' },
  { name: 'arrow', icon: <ArrowIcon />, title: 'Arrow' },
  { name: 'line', icon: <LineIcon />, title: 'Line' },
  { name: 'angle', icon: <AngleIcon />, title: 'Angle' },
  { name: 'circle', icon: <CircleOutlineIcon />, title: 'Circle' },
  { name: 'circle-fill', icon: <Circle />, title: 'Circle Fill' },
  { name: 'rectangle', icon: <RectangleOutlineIcon />, title: 'Square' },
  { name: 'rectangle-fill', icon: <SquareMenuIcon />, title: 'Square Fill' },
];
const brushSizes = [1, 3, 6, 12, 24];

interface AnnotationAreaFunctions {
  removeAllHistory(): void;
  removeHistoryItem(): void;
}

const DigitalCoachingTool = () => {
  const dispatch = useDispatch();
  const [isPlaying, setIsPlaying] = React.useState<boolean>(false);
  const playerRef = React.useRef<ReactPlayer | null>(null);
  const [loop, setLoop] = React.useState<boolean>(false);
  const [volume, setVolume] = React.useState<number>(0.5);
  const [speed, setSpeed] = React.useState<number>(1);
  const [marks, setMarks] = React.useState<{ value: number }[]>([]);
  const [url, setUrl] = React.useState<string>('');
  const [isVideo, setIsVideo] = React.useState<boolean>(false);
  const [selectedBrushType, setSelectedBrushType] = React.useState<BrushType>(brushTypes[0]);
  const [brushSize, setBrushSize] = React.useState<number>(3);
  const [highlight, setHighlight] = React.useState<boolean>(false);
  const [brushColors, setBrushColors] = React.useState<string[]>(['#B5E69A', '#F49E9E', '#3DCC96', '#FF3A3A', '#000000', '#0606A5']);
  const [brushColor, setBrushColor] = React.useState<string>('#0606A5');
  const annotationAreaRef = React.useRef<AnnotationAreaFunctions>(null);
  const [volumeAnchorEl, setVolumeAnchorEl] = React.useState<HTMLDivElement | null>(null);
  const [speedAnchorEl, setSpeedAnchorEl] = React.useState<HTMLDivElement | null>(null);
  const [showSideBar, setShowSideBar] = React.useState<boolean>(true);
  const isVolumePopperOpen = Boolean(volumeAnchorEl);
  const isSpeedPopperOpen = Boolean(speedAnchorEl);
  const initialProgress = {
    played: 0, playedSeconds: 0, loaded: 0, loadedSeconds: 0,
  };

  const { conversationId } = useParams();
  const conversationLoading = useSelector(selectConversationLoader);

  const fullscreenContainerRef = useRef(null);
  const mirroringStream = React.useRef<any>(undefined);
  const mirroringVideoEl = React.useRef<HTMLVideoElement>(null);
  const [mirroringScreen, setMirroringScreen] = React.useState<boolean>(false);

  // handle fullscreen
  const fullscreenHandler = useFullScreenHandle();
  const { active: fullscreen, enter, exit } = fullscreenHandler;

  const [progress, setProgress] = React.useState<Progress>(initialProgress);
  const [isSpeedMenuOpen, setIsSpeedMenuOpen] = React.useState<boolean>(false);
  const [showSpeedTooltip, setShowSpeedTooltip] = React.useState<boolean>(false);
  const playbackSpeedOptions = [
    { value: 0.25, label: '0.25x' },
    { value: 0.5, label: '0.5x' },
    { value: 0.75, label: '0.75x' },
    { value: 1, label: '1x' },
    { value: 1.25, label: '1.25x' },
    { value: 1.5, label: '1.5x' },
    { value: 1.75, label: '1.75x' },
    { value: 2, label: '2x' },
  ];

  const handleChange = (event: Event, newValue: number | number[]) => {
    const val = (newValue as number / 100);
    playerRef.current?.seekTo(parseFloat(String(val)), 'fraction');
  };

  const play = () => {
    setIsPlaying(true);
  };

  const pause = () => {
    setIsPlaying(false);
  };

  const stop = () => {
    setIsPlaying(false);
    playerRef.current?.seekTo(0);
  };

  const ProgressHandler = (progressData: Progress) => {
    // TODO: setProgress based seeking;
    setProgress(progressData);
  };

  const forward = () => {
    const currentTime = playerRef.current?.getCurrentTime() || 0;
    playerRef.current?.seekTo(currentTime + 5);
  };
  const backward = () => {
    const currentTime = playerRef.current?.getCurrentTime() || 0;
    playerRef.current?.seekTo(currentTime - 5);
  };

  const toggleLoop = () => {
    setLoop(!loop);
  };

  const clearMediaSelection = () => {
    stop();
    setMarks([]);
    setProgress(initialProgress);
    setIsVideo(false);
    setUrl('');
  };

  const handleStopMirroringScreen = () => {
    if (mirroringStream.current) {
      mirroringStream.current.getTracks().forEach((track: any) => track.stop());
      mirroringStream.current = undefined;
      setMirroringScreen(false);
    }
  };

  const handleStartMirroringScreen = async () => {
    try {
      if (isPlaying) {
        setIsPlaying(false);
      }

      const stream = (await navigator.mediaDevices.getDisplayMedia({
        video: {
          frameRate: 30,
        },
        audio: false,
      })) as MediaStream;

      if (!stream) {
        return;
      }

      const videoTrack = stream.getVideoTracks()[0];
      videoTrack.addEventListener('ended', () => {
        handleStopMirroringScreen();
        mirroringStream.current = undefined;
        setMirroringScreen(false);
      });

      mirroringStream.current = stream;
      clearMediaSelection();

      if (mirroringVideoEl.current) {
        mirroringVideoEl.current.srcObject = stream;
        setMirroringScreen(true);
      }
    } catch (err) {
      console.error('Stream Error:', err);
    }
  };

  const handleYoutubeImport = (videoUrl: string) => {
    // First stop current playback
    setIsPlaying(false);

    // Small timeout to ensure player state is reset before changing URL
    setTimeout(() => {
      stop();
      setMarks([]);
      setProgress(initialProgress);
      setIsVideo(true);
      setUrl(videoUrl);
      handleStopMirroringScreen();
      setMirroringScreen(false);

      // Set a small timeout to start playback after the player has loaded
      setTimeout(() => {
        if (playerRef.current) {
          playerRef.current.seekTo(0);
        }
      }, 300);
    }, 100);
  };

  const handleMediaSelect = React.useCallback((media: IMediaItemData) => {
    // First stop current playback
    setIsPlaying(false);

    // Small timeout to ensure player state is reset before changing URL
    setTimeout(() => {
      stop();
      setMarks([]);
      setProgress(initialProgress);
      setIsVideo(media.fileCategory === 'video');
      setUrl(media.contentUrl);
      handleStopMirroringScreen();
      setMirroringScreen(false);

      // Only attempt to play if it's a video
      if (media.fileCategory === 'video') {
        // Set a small timeout to start playback after the player has loaded
        setTimeout(() => {
          if (playerRef.current) {
            playerRef.current.seekTo(0);
          }
        }, 300);
      }
    }, 100);
  }, []);

  React.useEffect(() => {
    annotationAreaRef?.current?.removeAllHistory();
  }, [url]);

  React.useEffect(() => {
    if (conversationId) {
      dispatch(chatActions.setConversationId(conversationId));
      dispatch(chatActions.getSingleConversation());
    }
  }, [dispatch, conversationId]);

  const openVolumePopper = (event: any) => {
    setVolumeAnchorEl(event.currentTarget);
  };

  const closeVolumePopper = () => {
    setVolumeAnchorEl(null);
  };

  const openSpeedPopper = (event: any) => {
    setSpeedAnchorEl(event.currentTarget);
  };

  const closeSpeedPopper = () => {
    setSpeedAnchorEl(null);
  };

  const addMarkToTimeline = () => {
    setMarks((preMarks) => [...preMarks, { value: progress.played * 100 }]);
  };

  const usermessage = React.useMemo(
    () => <UserMessage />,
    [],
  );

  const usermedia = React.useMemo(
    () => <UserMedia onMediaSelect={handleMediaSelect} />,
    [handleMediaSelect],
  );

  const expertmedia = React.useMemo(
    () => <ExpertMedia onMediaSelect={handleMediaSelect} />,
    [handleMediaSelect],
  );

  return (
    <ExpertHubFullWidthWithoutTopbarLayout>
      <Loader loading={conversationLoading}>
        <Popover
          open={isVolumePopperOpen}
          anchorEl={volumeAnchorEl}
          onClose={closeVolumePopper}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          container={fullscreenContainerRef.current}
        >
          <div
            className="p-0"
            style={{
              backgroundColor: 'background.paper', border: '1px solid #E7E7F1', borderRadius: '6px', overflow: 'hidden',
            }}
          >
            <Stack sx={{ height: 70, my: 1 }} spacing={1} direction="column" alignItems="center">
              <Slider
                size="small"
                aria-label="Volume"
                orientation="vertical"
                valueLabelDisplay="off"
                value={volume * 100}
                onChange={(event: Event, newValue: number | number[]) => {
                  if (typeof newValue === 'number') {
                    setVolume(newValue / 100);
                  }
                }}
                sx={{ mb: 0.5 }}
              />
              <IconButton
                sx={{ p: 0 }}
                onClick={() => {
                  if (volume === 0) {
                    setVolume(0.5);
                  } else {
                    setVolume(0);
                  }
                }}
              >
                {volume === 0 ? (
                  <VolumeMuteFillIcon className="cursor-pointer" sx={{ color: 'primary.main', fontSize: 15 }} />
                ) : (
                  <VolumeFillIcon className="cursor-pointer" sx={{ color: 'primary.main', fontSize: 15 }} />
                )}
              </IconButton>
            </Stack>
          </div>
        </Popover>
        <FullScreen handle={fullscreenHandler} className={`row coaching-tool-main-wrap ${fullscreen ? 'fullscreen-enabled' : ''}`}>
          <Box
            className="coaching-tool-sidebar col-12 col-lg-3 col-xl-3 col-xxl-2"
            sx={{
              border: 1,
              borderColor: 'divider',
              p: 3,
              pb: 0,
              backgroundColor: 'background.paper',
              display: showSideBar ? 'block' : 'none',
            }}
          >
            {conversationId && (
            <>
              {usermessage}
              {usermedia}
            </>
            )}
            <YoutubeImport importHandler={handleYoutubeImport} />
            {expertmedia}
          </Box>

          <div className={`col-12 ${showSideBar ? 'col-lg-9 col-xl-9 col-xxl-10' : 'col-lg-12 col-xl-12 col-xxl-12'} d-flex flex-column position-relative`} ref={fullscreenContainerRef}>
            <TopBar
              enterFullScreen={enter}
              exitFullScreen={exit}
              fullscreen={fullscreen}
              showSideBar={showSideBar}
              container={fullscreenContainerRef.current}
              toggleSideBar={(isDisplay: boolean) => setShowSideBar(isDisplay)}
              onStartMirroringScreen={handleStartMirroringScreen}
              onStopMirroringScreen={handleStopMirroringScreen}
              isMirroringScreen={mirroringScreen}
              isPlaying={isPlaying}
              setIsPlaying={setIsPlaying}
            />
            <div className="video-content-container flex-fill mx-n2">
              <div className="d-flex flex-row align-items-center h-100 w-100 overflow-hidden">
                <div className="flex-grow-1 h-100 d-flex flex-column">
                  <div className={`video-wrapper ${!isVideo ? 'full-height-wrapper' : ''} d-flex align-items-center justify-content-center pt-3 pt-md-4 px-3 px-lg-4 px-xl-4 position-relative`}>
                    <video
                      ref={mirroringVideoEl}
                      autoPlay
                      playsInline
                      muted
                      className="mirroring-video-player"
                      style={{ objectFit: 'contain', display: mirroringScreen ? 'block' : 'none' }}
                    />
                    {(url && url !== '') || mirroringScreen ? (
                      <>
                        {url !== '' && isVideo && (
                          <VideoPlayer
                            isPlaying={isPlaying}
                            playerRef={playerRef}
                            progressHandler={ProgressHandler}
                            handleIsPlaying={setIsPlaying}
                            loop={loop}
                            url={url}
                            volume={volume}
                            playbackRate={speed}
                          />
                        )}

                        {url !== '' && !isVideo && (
                          <div className="image-player">
                            <img src={url} alt="test" />
                          </div>
                        )}

                        <AnnotationArea
                          ref={annotationAreaRef}
                          brushTypes={brushTypes}
                          selectedBrushType={selectedBrushType}
                          onBrushSelect={(brushType) => { setSelectedBrushType(brushType); }}
                          size={brushSize}
                          highlight={highlight}
                          color={brushColor}
                          fullscreen={fullscreen}
                          showSideBar={showSideBar}
                        />
                      </>
                    ) : (
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>Select a video or image</Typography>
                    )}
                  </div>
                  {url && url !== '' && isVideo ? (
                    <div className="video-slider px-3 px-lg-4 px-xl-5 mb-3">
                      <Box sx={{}}>
                        <Slider
                          aria-label="Volume"
                          value={(progress.played * 100)}
                          onChange={handleChange}
                          sx={sliderStyle}
                          min={0}
                          max={100}
                          marks={marks}
                        />
                      </Box>
                      <Box
                        sx={{
                          py: 1,
                          px: 2,
                          boxShadow: '0px 8px 60px #00000005',
                          borderRadius: 1.5,
                          backgroundColor: 'background.paper',
                          border: 1,
                          borderColor: 'divider',
                        }}
                        className="d-flex justify-content-between align-items-center"
                      >
                        <Stack alignItems="center" direction="row" spacing={1}>

                          {isPlaying ? (
                            <Tooltip title="Pause">
                              <IconButton sx={{ p: 0 }} onClick={pause}>
                                <PauseCircleIcon className="cursor-pointer" sx={{ color: 'text.secondary' }} />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Play">
                              <IconButton sx={{ p: 0 }} onClick={play}>
                                <PlayCircleIcon className="cursor-pointer" sx={{ color: 'text.secondary' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Restart playback">
                            <IconButton sx={{ p: 0 }} onClick={stop}>
                              <StopCircleIcon className="cursor-pointer" sx={{ color: 'text.secondary' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Skip back 5 seconds">
                            <IconButton sx={{ p: 0 }} onClick={backward}>
                              <BackwardFillIcon className="cursor-pointer" sx={{ color: 'text.secondary' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Skip forward 5 seconds">
                            <IconButton sx={{ p: 0 }} onClick={forward}>
                              <ForwardFillIcon className="cursor-pointer" sx={{ color: 'text.secondary' }} />
                            </IconButton>
                          </Tooltip>
                          <Typography variant="body2" sx={{ color: 'text.secondary', pl: 0.75 }}>
                            {/* {`${plyedTime.toFixed(2)} / 12:25`} */}
                            {`${format(progress.playedSeconds).toString()} / ${format(progress.loadedSeconds).toString()}`}
                          </Typography>
                        </Stack>
                        <Stack alignItems="center" direction="row" spacing={1.25}>
                          <Tooltip
                            title="Playback speed"
                            open={showSpeedTooltip && !isSpeedMenuOpen}
                          >
                            <Select
                              value={speed}
                              onChange={(e) => setSpeed(e.target.value as number)}
                              onOpen={() => setIsSpeedMenuOpen(true)}
                              onClose={() => {
                                setIsSpeedMenuOpen(false);
                                setShowSpeedTooltip(false);
                              }}
                              renderValue={(value) => (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <VideoSpeedFillIcon sx={{ fontSize: 24, color: 'primary.dark' }} />
                                  <Typography variant="body2" sx={{ color: 'primary.dark' }}>
                                    {playbackSpeedOptions.find((opt) => opt.value === value)?.label}
                                  </Typography>
                                </Box>
                              )}
                              onMouseEnter={() => setShowSpeedTooltip(true)}
                              onMouseLeave={() => setShowSpeedTooltip(false)}
                              size="small"
                              sx={{
                                minWidth: 80,
                                paddingRight: '1px',
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'flex-end',
                                height: 32,
                                color: 'text.secondary',
                                '& .MuiOutlinedInput-notchedOutline': {
                                  border: 'none',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'primary.main',
                                },
                                '& .MuiSelect-select': {
                                  padding: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'end',
                                },
                                '& .MuiSelect-icon': {
                                  display: 'none',
                                },
                                '& .MuiSelect-select.MuiSelect-select': {
                                  paddingRight: '0 !important',
                                },

                              }}
                            >
                              {playbackSpeedOptions.map((option) => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                  sx={{
                                    '&:hover': {
                                      backgroundColor: 'primary.light',
                                    },
                                    '&.Mui-selected': {
                                      backgroundColor: 'primary.light',
                                      '&:hover': {
                                        backgroundColor: 'primary.light',
                                      },
                                    },
                                  }}
                                >
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </Tooltip>

                          <Tooltip title={loop ? 'Repeat off' : 'Repeat on'}>
                            <IconButton sx={{ p: 0 }} onClick={toggleLoop}>
                              <RepeatFillIcon className="cursor-pointer" sx={{ color: !loop ? 'text.secondary' : 'primary.main' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Add a mark">
                            <IconButton sx={{ p: 0 }} onClick={addMarkToTimeline}>
                              <FolderFillIcon className="cursor-pointer" sx={{ color: 'text.secondary' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Volume">
                            <IconButton
                              sx={{ p: 0 }}
                              onClick={openVolumePopper}
                            >
                              {volume === 0 ? (
                                <VolumeMuteFillIcon className="cursor-pointer" sx={{ color: 'primary.main' }} />
                              ) : (
                                <VolumeFillIcon className="cursor-pointer" sx={{ color: 'text.secondary' }} />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Stack>

                      </Box>
                    </div>
                  ) : (null)}
                </div>
                {(url && url !== '') || mirroringScreen ? (
                  <div className="tools-wrapper">
                    <Tools
                      brushTypes={brushTypes}
                      selectedBrushType={selectedBrushType}
                      onBrushSelect={(brushType) => { setSelectedBrushType(brushType); }}
                      brushSizes={brushSizes}
                      selectedBrushSize={brushSize}
                      onBrushSizeChange={(size: number) => { setBrushSize(size); }}
                      highlight={highlight}
                      onToggleHighlight={(value: boolean) => { setHighlight(value); }}
                      brushColors={brushColors}
                      setBrushColors={
                        (color: string) => {
                          setBrushColors((prevColors) => [...prevColors, color]);
                        }
                      }
                      selectedBrushColor={brushColor}
                      onColorSelect={(color: string) => { setBrushColor(color); }}
                      onHistoryAllClear={() => {
                        annotationAreaRef.current?.removeAllHistory();
                      }}
                      onHistoryRedo={() => {
                        annotationAreaRef.current?.removeHistoryItem();
                      }}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </FullScreen>
      </Loader>
    </ExpertHubFullWidthWithoutTopbarLayout>
  );
};

export default DigitalCoachingTool;
